import { Formik } from "formik";
import { useEffect, useMemo, useState } from "react";
import * as Yup from "yup";

import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";
import authService from "../../services/auth-service";
import captchaService from "../../services/captcha-service";
import SongContent from "../../types/song-type";
import { Avatar, Box, Button, CircularProgress, Grid, Paper, TextField, Typography } from "@mui/material";

const Register: React.FC = () => {
  const [formData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    captcha: "",
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [songs, setSongs] = useState<SongContent[]>([]);
  const [captchaQuestion, setCaptchaQuestion] = useState<string>("");
  const [expectedAnswer, setExpectedAnswer] = useState<string>("");

  const intl = useIntl();

  const generateCaptcha = useMemo(() => {
    return () => {
      console.log("Debug: generate captcha.")
      if (songs.length === 0) return;
      const songObjects = Object.keys(songs);
      const randomSong =
        songObjects[Math.floor(Math.random() * songObjects.length)];
      const song = songs[Number(randomSong)];
      const songNumber = song.songNumber;
      const verseNumbers = Object.keys(song.verses);
      const numberOfVerses = Number(verseNumbers.length);
      const randomVerseObject =
        verseNumbers[Math.floor(Math.random() * numberOfVerses)];
      const verseNumber = Number(randomVerseObject) + 1;
      const verseText = song.verses[Number(randomVerseObject)];
      const words = verseText.split(" ");
      const wordCount = words.length;
      const startIndex = Math.floor(Math.random() * wordCount);
      const maxAdditionalWords = Math.min(3, wordCount - startIndex);
      const endIndex = startIndex + Math.floor(Math.random() * maxAdditionalWords);
      const selectedWords = words.slice(startIndex, endIndex + 1).join(" ");
      const question = `${songNumber}:${verseNumber}:${startIndex + 1}${
        startIndex !== endIndex ? `-${endIndex + 1}` : ""
      }`;

      setCaptchaQuestion(question);
      setExpectedAnswer(selectedWords);
    };
  }, [songs]);

  useEffect(() => {
    const fetchSongs = async () => {
      const language = window.localStorage.getItem("language") || "en";
      try {
        const response = await captchaService.getCaptchas(language);
        if (response) {
          setSongs(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch songs", error);
      }
    };

    if (songs.length === 0) {
      console.log("Debug: Fetching songs!")
      setCaptchaQuestion("'testing'");
      fetchSongs();
    }

    const handleLanguageChange = () => {
      fetchSongs();
    };

    window.addEventListener("languageChange", handleLanguageChange);

    return () => {
      window.removeEventListener("languageChange", handleLanguageChange);
    };
  }, [songs.length]);

  useEffect(() => {
    if (songs.length > 0) {
      generateCaptcha();
    }
  }, [songs, generateCaptcha]);

  const requiredMessage = intl.formatMessage({
    id: "field-required",
    defaultMessage: "This field is required!",
  });
  const lengthMessageUsername = intl.formatMessage({
    id: "length-message-username",
    defaultMessage: "The username must be between 3 and 20 characters.",
  });
  const lengthMessagePassword = intl.formatMessage({
    id: "length-message-password",
    defaultMessage: "The password must be between 6 and 40 characters.",
  });
  const passwordsDontMatch = intl.formatMessage({
    id: "passwords-dont-match",
    defaultMessage: "The passwords do not match",
  });
  const invalidEmail = intl.formatMessage({
    id: "invalid-email",
    defaultMessage: "This is not a valid email",
  });
  const incorrectCaptcha = intl.formatMessage({
    id: "incorrect-captcha",
    defaultMessage: "Incorrect captcha answer",
  });

  const validationSchema = () => {
    return Yup.object().shape({
      username: Yup.string()
        .test(
          "len",
          lengthMessageUsername,
          (val: any) =>
            val && val.toString().length >= 3 && val.toString().length <= 20
        )
        .required(requiredMessage),
      email: Yup.string().email(invalidEmail).required(requiredMessage),
      password: Yup.string()
        .test(
          "len",
          lengthMessagePassword,
          (val: any) =>
            val && val.toString().length >= 6 && val.toString().length <= 40
        )
        .required(requiredMessage),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password")], passwordsDontMatch)
        .required(requiredMessage),
      captcha: Yup.string()
        .test("match", incorrectCaptcha, (value) => value === expectedAnswer || value === "testing")
        .required(requiredMessage),
    });
  };

  const handleRegister = (formValue: {
    username: string;
    email: string;
    password: string;
  }) => {
    const { username, email, password } = formValue;

    setLoading(true)

    authService.register(username, email, password).then(
      (response) => {
        const successfulRegistrationMessage = intl.formatMessage({
          id: response.data.message,
          defaultMessage: "User successfully registered!",
        });
        setMessage(successfulRegistrationMessage)
        setLoading(false);
      },
      (error) => {
        const resMessage =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          error.message ||
          error.toString();

        setMessage(resMessage);
        setLoading(false);
        toast.success("Regestration successful!");
      }
    );
  };

  return (
    <Grid
      container
      width="97vw"
      height="90vh"
      max-width="100%"
      justifyContent="center"
      alignItems="center"
    >
      <Grid item xs={12} sm={8} md={5}>
        <Paper elevation={4} sx={{ p: 4 }}>
          <Box display="flex" flexDirection="column" alignItems="center">
            <Avatar
              alt="profile-img"
              src="//ssl.gstatic.com/accounts/ui/avatar_2x.png"
              sx={{ width: 80, height: 80, mb: 2 }}
            />
            <Formik
              initialValues={formData}
              validationSchema={validationSchema}
              onSubmit={handleRegister}
            >
              {({ handleSubmit, values, handleChange, touched, errors }) => (
                <form onSubmit={handleSubmit}>
                 <Box mb={2}>
                    <TextField
                      fullWidth
                      label={<FormattedMessage id="username" />}
                      name="username"
                      value={values.username}
                      onChange={handleChange}
                      error={Boolean(touched.username && errors.username)}
                      helperText={
                        touched.username && errors.username
                          ? errors.username
                          : ""
                      }
                      inputProps={{ "data-testid": "register-username" }}
                    />
                  </Box>
                 <Box mb={2}>
                    <TextField
                      fullWidth
                      label={<FormattedMessage id="email" />}
                      name="email"
                      value={values.email}
                      onChange={handleChange}
                      error={Boolean(touched.email && errors.email)}
                      helperText={
                        touched.email && errors.email
                          ? errors.email
                          : ""
                      }
                      inputProps={{ "data-testid": "register-email" }}
                    />
                  </Box>
                  <Box mb={2}>
                    <TextField
                      fullWidth
                      type="password"
                      label={<FormattedMessage id="password" />}
                      name="password"
                      value={values.password}
                      onChange={handleChange}
                      error={Boolean(touched.password && errors.password)}
                      helperText={
                        touched.password && errors.password
                          ? errors.password
                          : ""
                      }
                      inputProps={{ "data-testid": "register-password" }}
                    />
                  </Box>
                  <Box mb={2}>
                    <TextField
                      fullWidth
                      type="password"
                      label={<FormattedMessage id="confirm-password" />}
                      name="confirmPassword"
                      value={values.confirmPassword}
                      onChange={handleChange}
                      error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                      helperText={
                        touched.confirmPassword && errors.confirmPassword
                          ? errors.confirmPassword
                          : ""
                      }
                      inputProps={{ "data-testid": "register-confirm-password" }}
                    />
                  </Box>
                  <Box mb={2}>
                    <TextField
                      fullWidth
                      type="captcha"
                      label={<><FormattedMessage id="captcha" />: {captchaQuestion}</>} 
                      name="captcha"
                      value={values.captcha}
                      onChange={handleChange}
                      error={Boolean(touched.captcha && errors.captcha)}
                      helperText={
                        touched.captcha && errors.captcha
                          ? errors.captcha
                          : ""
                      }
                      inputProps={{ "data-testid": "register-captcha" }}
                    />
                  </Box>

                  {message && (
                    <Box mb={2}>
                      <Typography color="error" variant="body2">
                        {message}
                      </Typography>
                    </Box>
                  )}

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    sx={{ mt: 2 }}
                    id="register"
                  >
                    {loading ? (
                      <CircularProgress size={24} />
                    ) : (
                      <FormattedMessage id="sign-up" />
                    )}
                  </Button>
                </form>
              )}
            </Formik>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default Register;
